import { useEffect,useMemo,useState } from "react";
import { Box,Typography,Button,TextField } from "@mui/material";
import AdministratorsTable from "./AdminAdministrators/AdministratorsTable";
import AdministratorModal from "./AdminAdministrators/AdministratorModal";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const formatWhatsAppPhone=(phone)=>{
  const clean=String(phone||"").replace(/\D/g,"");
  if(clean.startsWith("593")) return clean;
  if(clean.startsWith("0")) return `593${clean.slice(1)}`;
  return clean;
};

export default function AdminAdministrators(){
  const {colors,typography}=useAppTheme();
  const {showSuccess,showError}=useAlert();
  const [administrators,setAdministrators]=useState([]);
  const [search,setSearch]=useState("");
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [modalOpen,setModalOpen]=useState(false);
  const [selectedAdministrator,setSelectedAdministrator]=useState(null);

  const loadAdministrators=async()=>{
    try{
      setLoading(true);
      const response=await fetch("/api/admins/",{credentials:"include"});
      const data=await response.json();

      if(!response.ok){
        throw new Error(data.error||"No se pudieron cargar los administradores");
      }

      setAdministrators(Array.isArray(data)?data:[]);
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadAdministrators();
  },[]);

  const filteredAdministrators=useMemo(()=>{
    const value=search.trim().toLowerCase();

    if(!value) return administrators;

    return administrators.filter((administrator)=>
      [
        administrator.nombre_completo,
        administrator.nombres,
        administrator.apellidos,
        administrator.correo,
        administrator.identificacion,
        administrator.telefono,
      ].some((field)=>String(field||"").toLowerCase().includes(value))
    );
  },[administrators,search]);

  const handleCreate=()=>{
    setSelectedAdministrator(null);
    setModalOpen(true);
  };

  const handleEdit=(administrator)=>{
    setSelectedAdministrator(administrator);
    setModalOpen(true);
  };

  const handleSave=async(data)=>{
    try{
      setSaving(true);

      const response=await fetch(
        selectedAdministrator
          ? `/api/admins/${selectedAdministrator.id}/`
          : "/api/admins/",
        {
          method:selectedAdministrator?"PATCH":"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken"),
          },
          body:JSON.stringify(data),
        }
      );

      const result=await response.json();

      if(!response.ok){
        const message=result.error||
          Object.values(result).flat().filter((value)=>typeof value==="string").join(" ")||
          "No se pudo guardar el administrador";

        throw new Error(message);
      }

      const isEditing=Boolean(selectedAdministrator);

      setModalOpen(false);
      setSelectedAdministrator(null);
      await loadAdministrators();

      if(isEditing){
        showSuccess("Administrador actualizado correctamente.");
        return;
      }

      showSuccess("Administrador creado correctamente.");

      if(result.temporary_password&&result.telefono){
        const phone=formatWhatsAppPhone(result.telefono);
        const message=[
          `Hola ${result.nombre_completo||result.nombres||""}.`,
          "",
          "Has sido registrado como administrador de la liga.",
          `Usuario: ${result.correo}`,
          `Clave temporal: ${result.temporary_password}`,
          "",
          "Ya puedes ingresar al sistema para comenzar la gestión.",
        ].join("\n");

        window.open(
          `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
          "_blank",
          "noopener,noreferrer"
        );
      }
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1.5,
          mb:2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},
              fontWeight:typography.weights.extraBold,
              color:colors.text,
            }}
          >
            Administradores
          </Typography>

          <Typography
            sx={{
              mt:0.4,
              fontSize:typography.sizes.md,
              color:colors.textSecondary,
            }}
          >
            Administra los responsables de la gestión de la liga.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleCreate}
          sx={{
            px:2.2,
            backgroundColor:colors.secondary,
            "&:hover":{
              backgroundColor:colors.secondaryHover,
            },
          }}
        >
          Nuevo administrador
        </Button>
      </Box>

      <Box
        sx={{
          mb:1.5,
          display:"flex",
          justifyContent:"flex-end",
        }}
      >
        <TextField
          value={search}
          onChange={(event)=>setSearch(event.target.value)}
          placeholder="Buscar administrador"
          sx={{
            width:{xs:"100%",sm:320},
            "& .MuiOutlinedInput-root":{
              backgroundColor:colors.surface,
            },
          }}
        />
      </Box>

      <AdministratorsTable
        administrators={filteredAdministrators}
        loading={loading}
        onEdit={handleEdit}
      />

      <AdministratorModal
        open={modalOpen}
        onClose={()=>{
          if(saving) return;
          setModalOpen(false);
          setSelectedAdministrator(null);
        }}
        onSave={handleSave}
        administrator={selectedAdministrator}
        saving={saving}
      />
    </Box>
  );
}